import { useId } from 'react';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { Button, ButtonLink } from '@poseidon/components/Input2';
import FilterPopover from '@poseidon/components/Input2/FilterPopover';
import { Arrows } from '@poseidon/components/StrokeIcons';

import { useAuthContext } from '~/auth/auth.context';
import { api } from '~/utils/api';

// Utility function for case-insensitive email comparison
const emailsEqual = (email1?: string | null, email2?: string | null): boolean => {
  if (!email1 || !email2) return false;
  return email1.toLowerCase() === email2.toLowerCase();
};

export const ImpersonationAlert = () => {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;
  const [auth, { startImpersonation, stopImpersonation }] = useAuthContext();

  // Extract teamId safely
  const teamId = auth.user ? auth.userTeam?.teamId || '' : '';
  
  // Fetch team users for the dropdown - only if we have a valid team
  // Use the canonical bare-{ teamId } input so this shares one React Query cache
  // entry with the pages that already fetch team users (templates, prompts) — two
  // different input shapes would fire two identical requests. We hide removed
  // users from the dropdown client-side below instead of via the `hideRemoved`
  // param (the route defaults pagination to limit 1000, so coverage is unchanged).
  const teamUsersQuery = api.team.teamUsers.useQuery(
    { teamId },
    {
      enabled: !!teamId,
    }
  );

  const impersonateMutation = api.superAdmin.impersonate.useMutation();

  if (auth.impersonation == null || auth.user == null || auth.userTeam == null) return null;

  const teamUsers = teamUsersQuery.data?.teamUsers ?? [];

  // Handler to stop impersonation and redirect to "/"
  const handleStopImpersonation = () => {
    stopImpersonation();
  };

  // Handler to switch to a different user
  const handleSwitchUser = async (userId: string) => {
    
    // Find the target user to get their email for comparison
    const targetUser = teamUsers.find(user => user.userId === userId);
    if (emailsEqual(targetUser?.userEmail, auth.impersonation?.email)) return; // Already impersonating this user

    // Store the current impersonation session before stopping
    const currentImpersonationSession = auth.impersonation;
    
    try {
      // Temporarily stop impersonation to make the superAdmin call with the original super admin context
      stopImpersonation();
      
      // Wait a bit for the impersonation to fully stop
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now make the impersonate call with the super admin context
      const result = await impersonateMutation.mutateAsync({ userId });
      if (result.impersonationToken) {
        // Use the user's email for the impersonation session (not display name)
        const targetEmail = targetUser?.userEmail;

        if (targetEmail) {
          // Use superadmin info from mutation result, falling back to current session.
          // The picture is the same team-scoped one the dropdown already renders for this user.
          startImpersonation(result.impersonationToken, targetEmail, userId, {
            name: result.superadminName ?? currentImpersonationSession?.superadminName,
            teamName: result.superadminTeamName ?? currentImpersonationSession?.superadminTeamName,
          }, targetUser?.profilePicture ?? null);
        }
      }
    } catch (error) {
      // Handle the specific case of trying to impersonate yourself
      if (error instanceof Error && error.message.includes('cannot impersonate yourself')) {
        // Don't restore impersonation - stay as the super admin
        return;
      }

      // If the switch failed for other reasons, try to restore the previous impersonation
      if (currentImpersonationSession) {
        startImpersonation(
          currentImpersonationSession.token,
          currentImpersonationSession.email,
          currentImpersonationSession.userId,
          {
            name: currentImpersonationSession.superadminName,
            teamName: currentImpersonationSession.superadminTeamName,
          },
          currentImpersonationSession.profilePicture,
        );
      }
    }
  };

  // Always show the impersonated user's information
  // On super admin pages, auth.userTeam shows the super admin, so we use data from auth.impersonation
  const displayName = auth.impersonation.email || auth.userTeam.userTeamName || auth.userTeam.userTeamEmail;

  // Create a userTeam object for the impersonated user to pass to TeamUserAvatar
  const impersonatedUserTeam = {
    profilePicture: auth.impersonation.profilePicture ?? null,
    userTeamName: auth.impersonation.email,
    userTeamEmail: auth.impersonation.email,
  };

  // Create dropdown options for team users
  // Filter out users that should not be shown in the dropdown
  const dropdownOptions = teamUsers
    .filter(user => {
      // Hide removed users from the switcher (previously done server-side via hideRemoved)
      if (user.userTeamStatus === 'removed') return false;
      // Don't show the currently impersonated user (compare by email)
      if (emailsEqual(user.userEmail, auth.impersonation?.email)) return false;
      return true;
    })
    .map(user => ({
      value: user.userId,
      label: user.userTeamName || user.userEmail,
      icon: <TeamUserAvatar userTeam={user} className="size-4 text-3xs" shape="circle" />,
    }));

  // Find currently impersonated user ID
  const currentImpersonatedUserId = teamUsers.find(user => 
    emailsEqual(user.userEmail, auth.impersonation?.email)
  )?.userId;

  return (
    <div className="bg-linear-to-r from-blue-500 via-purple-500 to-red-500 text-white py-3 px-4 w-full shadow-lg" role="alert">
      <div className="flex items-center gap-4">
        <TeamUserAvatar
          userTeam={impersonatedUserTeam}
          className="size-12"
          shape="circle"
        />
        <div className="flex-1">
          <p className="font-bold text-base leading-tight">
            You are currently acting on behalf of the Creator: <strong>{displayName}</strong>
          </p>
          <p className="text-sm opacity-70 leading-tight mt-0.5">
            Please make sure you are NOT logged in with a Chrome extension on LinkedIn unless you are also logged in as the creator.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ButtonLink
            href={{ pathname: '/superadmin/doneforyou', query: {} }}
            size="sm"
            className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
            icon={<Arrows.ArrowLeft className="text-white" />}
          >
            Done For You
          </ButtonLink>
          {dropdownOptions.length > 0 && (
            <>
              <Button
                popoverTarget={popoverId}
                style={{ anchorName } as React.CSSProperties}
                size="sm"
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                icon={<TeamUserAvatar
                  userTeam={impersonatedUserTeam}
                  className="size-4"
                  shape="circle"
                />}
                rIcon={<Arrows.ChevronDown className="text-white" />}
              >
                Switch
              </Button>
              <FilterPopover
                popoverId={popoverId}
                anchorName={anchorName}
                size="sm"
                items={dropdownOptions}
                selectedValue={currentImpersonatedUserId}
                onSelect={handleSwitchUser}
              />
            </>
          )}
          <Button 
            onClick={handleStopImpersonation} 
            size="sm"
            className="bg-white text-gray-800 hover:bg-gray-100"
          >
            Stop Impersonating
          </Button>
        </div>
      </div>
    </div>
  );
};
